class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.isOpen = false;
    this._submitting = false;
    this.dataType = this.querySelector(
      'input[type="search"]',
    ).dataset.objectType;
    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
    // Mobile Chrome fires 'search' event for the keyboard Go/Search button
    // instead of triggering form submit — handle it explicitly
    this.input.addEventListener("search", this.onSearchEvent.bind(this));
  }

  onSearchEvent() {
    if (!this.getQuery().length) return;
    this._doSubmit();
  }

  _doSubmit() {
    if (this._submitting) return;
    this._submitting = true;
    const form = this.querySelector("form.search");
    if (form.requestSubmit) {
      form.requestSubmit();
    } else {
      window.location.assign(
        form.action + "?q=" + encodeURIComponent(this.getQuery()),
      );
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    // Use event.key instead of event.code — iOS Safari virtual keyboard
    // fires keyup with event.code === "" but event.key is correctly populated.
    switch (event.key) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        // If no predictive result is selected, submit the form explicitly.
        // iOS Safari virtual keyboard does not reliably fire the native form
        // submit or the 'search' event, so we handle it here as a fallback.
        // _doSubmit() is guarded to prevent a double-submit when the 'search'
        // event fires on the same keystroke (Android Chrome / iOS Safari).
        if (
          !this.querySelector(
            '[aria-selected="true"] a, [aria-selected="true"] button',
          ) &&
          this.getQuery().length
        ) {
          this._doSubmit();
        }
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll("li");
    let activeElement = this.querySelector("li");

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    var searchObj = {
      q: searchTerm,
      "resources[type]": this.dataType,
      "resources[options][fields]":
        "title,product_type,variants.title,variants.sku,tag,vendor,author,body,variants.barcode",
      section_id: "predictive-search",
    };

    function paramUrl(obj) {
      return Object.keys(obj)
        .map(function (key) {
          return key + "=" + encodeURIComponent(obj[key]);
        })
        .join("&");
    }

    var params = paramUrl(searchObj);
    //console.log(routes.predictive_search_url);
    const fetchUrl =
      (this.dataType == "product"
        ? routes.predictive_search_url
        : "/search/suggest.json") +
      "?" +
      params +
      (this.dataType == "product" ? "&resources[limit]=4" : "");
    //console.log("fetchUrl", fetchUrl);
    fetch(fetchUrl)
      .then((response) => {
        if (this.dataType == "product") {
          return response.text();
        } else if (this.dataType == "article") {
          return response.json();
        }
      })
      .then((text) => {
        let resultsMarkup;
        if (this.dataType == "product") {
          resultsMarkup = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector("#shopify-section-predictive-search").innerHTML;
        } else if (this.dataType == "article") {
          const articles = text.resources.results.articles;
          let results = "";
          if (articles.length == 0) {
            results =
              "<li class='predictive-search__list-item'><span class='predictive-search__item'>No results.</span> </li>";
          } else {
            for (let i = 0; i < articles.length; i++) {
              results += `<li id="predictive-search-option-${
                i + 1
              }" class="predictive-search__list-item" role="option" aria-selected="false">
              
            <a href="${
              articles[i].url
            }" class="predictive-search__item predictive-search__item--link link link--text" tabindex="-1">
            ${
              articles[i].featured_image.url
                ? `<img class="predictive-search__image"
            src="${articles[i].featured_image.url}"
            alt="${articles[i].featured_image.alt}"
            width="50"
            height="50.0"
          >`
                : ""
            }
            <div class="predictive-search__item-content predictive-search__item-content--centered"><h3 class="predictive-search__item-heading h5">${
              articles[i].title
            }</h3></div>
            </a>
          </li>`;
            }
          }
          //  console.log(results);
          resultsMarkup = `<div id="shopify-section-predictive-search" class="shopify-section">
          <div id="predictive-search-results"><h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">
                  Articles
          
                  <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </h2>
                <ul id="predictive-search-results-list" class="predictive-search__results-list list-unstyled" role="listbox" aria-labelledby="predictive-search-articles">
                ${results}
               
              </ul>
          
              <div class="predictive-search__loading-state" aria-hidden="true">
                <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
            </div>
          
            <span class="hidden" data-predictive-search-live-region-count-value>${articles.length} results
          </span></div>`;
        }

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
        // console.log(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.getElementById("shopify-section-header").getBoundingClientRect()
        .bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);

// Ensure the predictive search modal closes when the close button is clicked
document.addEventListener("DOMContentLoaded", function () {
  const closeButton = document.querySelector(".search-modal__close-button");
  if (closeButton) {
    closeButton.addEventListener("click", function () {
      // Find the closest <details> ancestor and close it
      const details = closeButton.closest("details");
      if (details) {
        details.removeAttribute("open");
      }
    });
  }
});
